import { AlertCircle, Check, Loader2, Search } from 'lucide-react';
import { useTranslation, type TranslationKey } from '@dukanti/shared/i18n';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { DESTINATION_FOCUS_RING_INSET } from './focus-ring';

/** Shared pieces of the category and product pickers (lifted from the ads pickers). */

export function PickerSearch({
  id,
  label,
  placeholder,
  value,
  onChange,
  testId,
}: {
  readonly id: string;
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly testId: string;
}): React.JSX.Element {
  return (
    <div>
      <Label htmlFor={id} className="mb-1.5 block text-start">
        {label}
      </Label>
      <div className="relative">
        <Search
          className="text-muted-foreground pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          id={id}
          type="search"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          className="ps-9"
          placeholder={placeholder}
          autoComplete="off"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

/**
 * The current pick. Its name comes from the store's own lists, so it shows
 * even when it is outside the current page of results. A pick the server
 * reports as gone (PD10) shows "Unavailable" and keeps its id.
 */
export function PickerSelection({
  text,
  unavailable,
  testIdPrefix,
}: {
  readonly text: string;
  readonly unavailable: boolean;
  readonly testIdPrefix: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'bg-admin-surface-muted rounded-lg border p-3 text-start',
        unavailable ? 'border-amber-300 dark:border-amber-500/40' : 'border-border',
      )}
      data-testid={`${testIdPrefix}-selection`}
      data-unavailable={unavailable ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-foreground min-w-0 break-words text-sm font-medium">{text}</p>
        {unavailable ? (
          <Badge variant="warning" data-testid={`${testIdPrefix}-unavailable`}>
            {t('admin.destination.unavailableBadge')}
          </Badge>
        ) : null}
      </div>
      {unavailable ? (
        <p className="text-muted-foreground mt-1 text-xs">
          {t('admin.destination.unavailable')} {t('admin.destination.unavailableNext')}
        </p>
      ) : null}
    </div>
  );
}

export function PickerStatus({
  isLoading,
  isError,
  isEmpty,
  loadingKey,
  retryKey,
  emptyKey,
  onRetry,
  testIdPrefix,
}: {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isEmpty: boolean;
  readonly loadingKey: TranslationKey;
  readonly retryKey: TranslationKey;
  readonly emptyKey: TranslationKey;
  readonly onRetry: () => void;
  readonly testIdPrefix: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div
        role="status"
        className="text-muted-foreground flex items-center gap-2 text-start text-sm"
        data-testid={`${testIdPrefix}-loading`}
      >
        <Loader2 className="size-4 animate-spin" aria-hidden />
        <span>{t(loadingKey)}</span>
      </div>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="border-destructive/25 bg-destructive/10 text-destructive flex flex-wrap items-start justify-between gap-3 rounded-lg border p-3 text-start text-sm"
        data-testid={`${testIdPrefix}-error`}
      >
        <span className="flex min-w-0 items-start gap-2">
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {t('common.errors.unknown' as TranslationKey)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={onRetry}
          data-testid={`${testIdPrefix}-retry`}
        >
          {t(retryKey)}
        </Button>
      </div>
    );
  }
  if (isEmpty) {
    return (
      <p
        className="text-muted-foreground rounded-lg border border-dashed p-4 text-start text-sm"
        data-testid={`${testIdPrefix}-empty`}
      >
        {t(emptyKey)}
      </p>
    );
  }
  return null;
}

export function PickerOption({
  selected,
  onSelect,
  testId,
  children,
}: {
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly testId: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'hover:bg-admin-surface-muted flex min-h-11 w-full items-start justify-between gap-3 px-4 py-3 text-start text-sm outline-none transition-colors',
        DESTINATION_FOCUS_RING_INSET,
        selected && 'bg-admin-surface-muted',
      )}
      data-testid={testId}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {selected ? <Check className="text-zaatar mt-0.5 size-4 shrink-0" aria-hidden /> : null}
    </button>
  );
}

export type PickerPromptTone = 'neutral' | 'error';

/**
 * UI/UX P2-2: what to do while nothing is picked. A neutral hint after the
 * card is chosen; an error only once the merchant tried to finish without a
 * pick. Pickers show it only next to a list there is something to pick from.
 */
export function PickerPrompt({
  text,
  tone,
  testId,
}: {
  readonly text: string;
  readonly tone: PickerPromptTone;
  readonly testId: string;
}): React.JSX.Element {
  return tone === 'error' ? (
    <p
      role="alert"
      className="text-destructive text-start text-xs font-medium"
      data-testid={testId}
      data-tone="error"
    >
      {text}
    </p>
  ) : (
    <p
      className="text-muted-foreground text-start text-xs"
      data-testid={testId}
      data-tone="neutral"
    >
      {text}
    </p>
  );
}
